import { VendorCreate } from '../../clients/rust-api-client';

/**
 * The create-a-vendor modal's draft, and the pure rules over it (#665).
 * Only the association and a name are required; everything else is
 * optional, as it was in the old dialog.
 */
export type VendorStatus = 'active' | 'inactive';

export const VENDOR_STATUSES: { value: VendorStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export interface VendorDraft {
  hoaId: string | null;
  name: string;
  description: string;
  status: VendorStatus;
  serviceTypes: string;
  contactName: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  city: string;
  state: string | null;
  zip: string;
  notes: string;
}

export const EMPTY_VENDOR_DRAFT: VendorDraft = {
  hoaId: null,
  name: '',
  description: '',
  status: 'active',
  serviceTypes: '',
  contactName: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  city: '',
  state: null,
  zip: '',
  notes: '',
};

export const NAME_MAX = 255;
export const PHONE_MAX = 20;
export const ZIP_MAX = 10;

/** Required and empty, as "Still needs …" phrases. */
export const missingForVendor = (d: VendorDraft): string[] =>
  [!d.hoaId && 'an association', !d.name.trim() && 'a name'].filter(
    Boolean
  ) as string[];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Fields that are filled in but wrong, keyed by field. */
export const vendorFieldErrors = (
  d: VendorDraft
): Partial<Record<keyof VendorDraft, string>> => {
  const errors: Partial<Record<keyof VendorDraft, string>> = {};
  if (d.name.trim().length > NAME_MAX)
    errors.name = `At most ${NAME_MAX} characters`;
  const email = d.email.trim();
  if (email && !EMAIL.test(email)) errors.email = 'Not an email address';
  if (d.phone.trim().length > PHONE_MAX)
    errors.phone = `At most ${PHONE_MAX} characters`;
  if (d.zip.trim().length > ZIP_MAX) errors.zip = `At most ${ZIP_MAX} characters`;
  return errors;
};

/**
 * The request for a complete, valid draft, or null. Empty optional fields
 * go as null, not as empty strings. Tax ID, insurance and payment terms
 * are not asked for here; they are set on the vendor's own page.
 */
export const buildVendorCreate = (d: VendorDraft): VendorCreate | null => {
  if (missingForVendor(d).length || !d.hoaId) return null;
  if (Object.keys(vendorFieldErrors(d)).length) return null;
  const text = (v: string | null) => (v && v.trim() ? v.trim() : null);
  return {
    hoa_id: d.hoaId,
    name: d.name.trim(),
    description: text(d.description),
    status: d.status,
    service_types: text(d.serviceTypes),
    contact_name: text(d.contactName),
    phone: text(d.phone),
    email: text(d.email),
    website: text(d.website),
    address: text(d.address),
    city: text(d.city),
    state: text(d.state),
    zip: text(d.zip),
    notes: text(d.notes),
    tax_id: null,
    insurance_policy_number: null,
    insurance_expiry: null,
    payment_terms: null,
  };
};
